import {ExpandMore, Search, UnfoldLess, UnfoldMore} from '@mui/icons-material';
import {Box, Button, Checkbox, Collapse, OutlinedInput, Stack, Typography} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import AMPModal from 'src/components/common/AMPModal';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {actionsWrapperSx} from 'src/components/forms/AddReManager/AddReManager.sx';
import {searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {PermissionDTO} from 'src/types';
import theme from 'src/theme';
import {IAM_TONE, pillSx, subjectBandSx} from '../iam.sx';

interface PermissionPickerModalProps {
    /** The whole catalog. Anything already held is filtered out here, not by the caller. */
    catalog: PermissionDTO[];
    /** Permission names the target already has. */
    held: Set<string>;
    title: string;
    /** Closing without applying. Named for the button, because that is the only thing it does. */
    onClose: () => void;
    /**
     * Applies the picks. Both callers persist inside it rather than staging, so the modal's own button
     * is the commit - see the note on the footer below.
     */
    onAdd: (names: string[]) => void;
    /** A commit in flight, so the button can refuse a second one. */
    saving?: boolean;
}

/**
 * Choosing permissions to add.
 *
 * The list contains only what is missing, and that is the whole design. The screen this replaces put
 * all 148 catalog entries on the page as checkboxes, which made "granted" and "available" the same
 * visual object — you could not see what a role had, and there was no verb anywhere for adding or
 * removing. Once the list is only ever things you do not have, a tick can mean exactly one thing.
 *
 * Subjects start collapsed. Thirty groups fully expanded is a wall of two-line rows several screens
 * tall, and scrolling it is how you lose track of what you already ticked; collapsed, the whole
 * catalog fits in one view and the shape of it is legible. Searching expands everything, because a
 * search is a statement that you want to see matches rather than browse.
 *
 * Descriptions are shown here rather than on the role page, because this is the moment the decision is
 * made; afterwards the name is enough to recognise it by. A collapsed subject still reports how many
 * of its permissions are ticked, so nothing chosen can hide behind a closed group.
 */
export const PermissionPickerModal: FC<PermissionPickerModalProps> = ({
    catalog,
    held,
    title,
    onClose,
    onAdd,
    saving = false,
}) => {
    const intl = useIntl();
    const [keyword, setKeyword] = useState('');
    const [chosen, setChosen] = useState<Set<string>>(new Set());
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const searching = keyword.trim().length > 0;

    const bySubject = useMemo(() => {
        const needle = keyword.trim().toLowerCase();
        const groups = new Map<string, PermissionDTO[]>();
        catalog
            .filter((permission) => !held.has(permission.name))
            .filter(
                (permission) =>
                    !needle ||
                    permission.name.toLowerCase().includes(needle) ||
                    (permission.description ?? '').toLowerCase().includes(needle)
            )
            .forEach((permission) => {
                const list = groups.get(permission.subject) ?? [];
                list.push(permission);
                groups.set(permission.subject, list);
            });
        return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [catalog, held, keyword]);

    const listed = useMemo(
        () => bySubject.reduce((total, [, group]) => total + group.length, 0),
        [bySubject]
    );

    const allExpanded = bySubject.length > 0 && bySubject.every(([subject]) => expanded.has(subject));

    const toggle = (name: string) => {
        const next = new Set(chosen);
        if (!next.delete(name)) {
            next.add(name);
        }
        setChosen(next);
    };

    const toggleSubject = (group: PermissionDTO[], checked: boolean) => {
        const next = new Set(chosen);
        group.forEach((permission) =>
            checked ? next.add(permission.name) : next.delete(permission.name)
        );
        setChosen(next);
    };

    const toggleExpanded = (subject: string) => {
        const next = new Set(expanded);
        if (!next.delete(subject)) {
            next.add(subject);
        }
        setExpanded(next);
    };

    return (
        <AMPModal open handleClose={onClose} title={title} type="md" fullWidth withoutPaddings>
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <OutlinedInput
                    sx={searchInputSx}
                    color="secondary"
                    type="text"
                    name="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder={intl.formatMessage({id: 'search'})}
                    startAdornment={<Search/>}
                />

                {/*
                    Says what the list is, not a statistic beside it. "N not yet granted" was read as a
                    tally standing next to the whole catalog - so the list looked unfiltered and the
                    obvious next question was where the filter had gone. Everything held is already
                    left out; naming the rows for what you can do with them says so without a sentence.
                */}
                <Typography sx={pillSx(IAM_TONE.none)}>
                    {intl.formatMessage(
                        {id: 'accessControlAvailableToAdd', defaultMessage: '{count} available to add'},
                        {count: listed}
                    )}
                </Typography>

                {/* Hidden while searching: search already opens every group, so the control would
                    claim to do something it cannot. */}
                {!searching && bySubject.length > 0 && (
                    <Button
                        size="small"
                        sx={{ml: 'auto', textTransform: 'none'}}
                        startIcon={allExpanded ? <UnfoldLess/> : <UnfoldMore/>}
                        onClick={() =>
                            setExpanded(
                                allExpanded ? new Set() : new Set(bySubject.map(([subject]) => subject))
                            )
                        }
                    >
                        {allExpanded
                            ? intl.formatMessage({
                                id: 'accessControlCollapseAll',
                                defaultMessage: 'Collapse all',
                            })
                            : intl.formatMessage({
                                id: 'accessControlExpandAll',
                                defaultMessage: 'Expand all',
                            })}
                    </Button>
                )}
            </Box>

            <Box sx={{maxHeight: 460, overflowY: 'auto'}}>
                {bySubject.length === 0 && (
                    <Typography sx={{px: 2.5, py: 5, textAlign: 'center', color: 'grey.500'}}>
                        {intl.formatMessage({
                            id: 'accessControlNothingLeftToAdd',
                            defaultMessage: 'Nothing left to add here.',
                        })}
                    </Typography>
                )}

                {bySubject.map(([subject, group]) => {
                    const picked = group.filter((permission) => chosen.has(permission.name)).length;
                    const open = searching || expanded.has(subject);
                    return (
                        <Box key={subject}>
                            <Box
                                sx={{
                                    ...subjectBandSx,
                                    cursor: 'pointer',
                                    transition: 'background-color 120ms ease',
                                    '&:hover': {backgroundColor: theme.palette.primaryColor.A50},
                                }}
                                onClick={() => toggleExpanded(subject)}
                            >
                                <Checkbox
                                    size="small"
                                    sx={{p: 0}}
                                    checked={picked === group.length}
                                    indeterminate={picked > 0 && picked < group.length}
                                    // Ticking a whole subject and opening it are different intents, and
                                    // the checkbox sits inside the row that does the opening.
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) => toggleSubject(group, event.target.checked)}
                                />
                                <ExpandMore
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.neutral[400],
                                        transition: 'transform 160ms ease',
                                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    }}
                                />
                                <Typography sx={{fontWeight: 600, fontSize: '0.875rem'}}>
                                    {intl.formatMessage({
                                        id: `permissionSubject.${subject}`,
                                        defaultMessage: subject,
                                    })}
                                </Typography>
                                <Typography
                                    sx={{
                                        ...pillSx(picked ? IAM_TONE.granted : IAM_TONE.none),
                                        ml: 'auto',
                                    }}
                                >
                                    {picked
                                        ? intl.formatMessage(
                                            {
                                                id: 'accessControlNSelected',
                                                defaultMessage: '{count} selected',
                                            },
                                            {count: picked}
                                        )
                                        : group.length}
                                </Typography>
                            </Box>

                            <Collapse in={open} unmountOnExit>
                                {group.map((permission) => (
                                    <Box
                                        key={permission.name}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            px: 2.5,
                                            py: 1.25,
                                            cursor: 'pointer',
                                            borderBottom: `1px solid ${theme.palette.neutral[100]}`,
                                            transition: 'background-color 120ms ease',
                                            '&:hover': {backgroundColor: theme.palette.primaryColor.A50},
                                        }}
                                        onClick={() => toggle(permission.name)}
                                    >
                                        <Checkbox
                                            size="small"
                                            sx={{p: 0, mt: 0.25}}
                                            checked={chosen.has(permission.name)}
                                            onChange={() => toggle(permission.name)}
                                        />
                                        <Box sx={{minWidth: 0, flex: 1}}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2" sx={{fontWeight: 500}}>
                                                    {intl.formatMessage({
                                                        id: `permission.${permission.subject}.${permission.action}`,
                                                        defaultMessage: permission.name,
                                                    })}
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8125rem',
                                                    color: theme.palette.grey[500],
                                                }}
                                            >
                                                {permission.description ?? permission.name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>

            {/*
                Discard and Save, not Cancel and Add.

                Adding used to close the modal and leave the picks staged behind a second Save at the
                foot of the page, so the button that looked like the end of the task was the middle of
                it - and the step that mattered was somewhere the eye had already left. One pair of
                verbs, in the place the decision is made, and it is done.
            */}
            <Box sx={actionsWrapperSx(theme)}>
                <CancelButton text="accessControlDiscardChanges" handler={onClose}/>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    {chosen.size > 0 && (
                        <Typography sx={pillSx(IAM_TONE.granted)}>
                            {intl.formatMessage(
                                {id: 'accessControlNSelected', defaultMessage: '{count} selected'},
                                {count: chosen.size}
                            )}
                        </Typography>
                    )}
                    {/* `isSubmitting` is this component's only disable lever - it maps straight to the
                        button's `disabled`. Nothing to submit is as good a reason as a submit in
                        flight. */}
                    <SubmitButton
                        text="accessControlSaveChanges"
                        color="brand"
                        isSubmitting={saving || chosen.size === 0}
                        handler={() => onAdd(Array.from(chosen))}
                    />
                </Stack>
            </Box>
        </AMPModal>
    );
};
